// Admin: this year's blåtur organizers, handing over to next year's, and who is next in the rotation

import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs, doc, setDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { app } from './firebase-config';
import { BLATUR_TYPE, tripYear } from './blaturData';

const db = getFirestore(app);

function AdminBlatur({ user, roles, directory }) {
  const [trips, setTrips] = useState([]);
  const current = roles.config;
  // Super admins edit the current organizers; organizers pick their successors from scratch
  const [selected, setSelected] = useState(roles.isSuperAdmin ? current?.organizers || [] : []);
  const [year, setYear] = useState((current?.year || new Date().getFullYear()) + (roles.isSuperAdmin ? 0 : 1));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'events'), where('event_type', '==', BLATUR_TYPE)))
      .then(snap => setTrips(snap.docs.map(d => d.data())));
  }, []);

  const accounts = useMemo(
    () => [...directory.users].sort((a, b) => a.name.localeCompare(b.name, 'no')),
    [directory.users]
  );
  const nameOf = (uid) => accounts.find(u => u.id === uid)?.name || 'Ukjent bruker';

  // Rule 4b: nobody hosts again before everyone has hosted. Shows who has hosted the fewest times.
  const rotation = useMemo(() => {
    const stats = {};
    trips.forEach(trip => {
      (trip.Deltagelse || []).forEach(n => { stats[n.trim()] = stats[n.trim()] || { name: n.trim(), hosted: 0, last: null, trips: 0 }; });
      (trip.Vert || []).forEach(n => { stats[n.trim()] = stats[n.trim()] || { name: n.trim(), hosted: 0, last: null, trips: 0 }; });
      new Set([...(trip.Deltagelse || []), ...(trip.Vert || [])].map(n => n.trim())).forEach(n => { stats[n].trips += 1; });
      (trip.Vert || []).forEach(n => {
        const s = stats[n.trim()];
        s.hosted += 1;
        s.last = Math.max(s.last || 0, tripYear(trip));
      });
    });
    const latestYear = Math.max(0, ...trips.map(tripYear));
    // Active members: on a trip in the last three years
    const recent = new Set(trips.filter(t => tripYear(t) >= latestYear - 2)
      .flatMap(t => [...(t.Deltagelse || []), ...(t.Vert || [])].map(n => n.trim())));
    return Object.values(stats)
      .filter(s => recent.has(s.name))
      .sort((a, b) => a.hosted - b.hosted || (a.last || 0) - (b.last || 0) || b.trips - a.trips);
  }, [trips]);

  const toggle = (uid) => setSelected(s => (s.includes(uid) ? s.filter(x => x !== uid) : [...s, uid]));

  const save = async () => {
    if (selected.length === 0) return alert('Velg minst én arrangør.');
    const names = selected.map(nameOf).join(' og ');
    const losingAccess = !roles.isSuperAdmin && !selected.includes(user.uid);
    const message = losingAccess
      ? `Overlevere blåtur ${year} til ${names}? Du mister da admin-tilgangen til blåtur.`
      : `Sette ${names} som arrangører av blåtur ${year}?`;
    if (!window.confirm(message)) return;
    try {
      setSaving(true);
      await setDoc(doc(db, 'config', 'blatur'), {
        ...(current || {}),
        organizers: selected,
        year: parseInt(year, 10),
        updatedBy: user.uid,
        updatedAt: serverTimestamp(),
      });
      await roles.reload();
    } catch (error) {
      alert('Kunne ikke lagre: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-blatur">
      <section className="admin-form">
        <h2>Arrangører nå</h2>
        {current?.organizers?.length ? (
          <p>Blåtur {current.year}: <strong>{current.organizers.map(nameOf).join(', ')}</strong></p>
        ) : (
          <p className="admin-hint">Ingen arrangører er satt ennå.</p>
        )}
        <p className="admin-hint">Arrangørene har admin-tilgang til blåtur. Njaal og Matias har alltid full tilgang.</p>
      </section>

      <section className="admin-form">
        <h2>{roles.isSuperAdmin ? 'Sett arrangører' : 'Overlever til neste års arrangører'}</h2>
        <p className="admin-hint">
          Bare personer med bruker kan få admin-tilgang. Ifølge reglementet utnevnes neste års arrangører under turen.
        </p>
        <div className="admin-row">
          <div className="admin-field small">
            <label>År</label>
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
          </div>
        </div>
        <div className="admin-checklist">
          {accounts.map(u => (
            <label key={u.id} className={selected.includes(u.id) ? 'checked' : ''}>
              <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} />
              {u.name}
            </label>
          ))}
        </div>
        <div className="admin-actions">
          <button className="button-17" onClick={save} disabled={saving}>{saving ? 'Lagrer…' : 'Lagre arrangører'}</button>
        </div>
      </section>

      <section className="admin-form">
        <h2>Hvem står for tur?</h2>
        <p className="admin-hint">
          En tidligere arrangør kan ikke velges igjen før alle har arrangert. Listen viser aktive blåturere (med på tur de siste tre årene),
          sortert etter hvor få ganger de har arrangert.
        </p>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Navn</th><th>Arrangert</th><th>Sist</th><th>Turer</th></tr></thead>
            <tbody>
              {rotation.map(s => (
                <tr key={s.name} className="static">
                  <td>{s.name}</td><td>{s.hosted}</td><td>{s.last || '–'}</td><td>{s.trips}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminBlatur;
